import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t, formatPeriod } from '../../utils/creatorLang';
import { useCreatorAuth } from '../../utils/useCreatorAuth';
import {
  CreatorScreen, Card, Pill, BottomNav, ScreenHeader,
  I, SectionLabel, DiamondGem, TierBadge,
} from '../../components/creator/primitives';

const EVOLUTION_TIERS = [
  { pct: 4, days: 11, hours: 30 },
  { pct: 5, days: 15, hours: 40 },
  { pct: 6, days: 18, hours: 60 },
  { pct: 7, days: 22, hours: 80 },
];

const MAINTIEN_TIERS = [
  { pct: 2.5, days: 11, hours: 30 },
  { pct: 3.5, days: 15, hours: 40 },
  { pct: 4, days: 18, hours: 60 },
  { pct: 5, days: 22, hours: 80 },
];

const fmtD = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(Math.round(n));
};
const fmtNum = (n) => (n == null ? '0' : Number(n).toLocaleString('en-US'));

const CreatorCashback = () => {
  const navigate = useNavigate();
  const { tiktokId: authTiktokId, loading: authLoading, isDebugMode } = useCreatorAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (authLoading || !authTiktokId) return;
    const init = async () => {
      setLoading(true);
      try {
        const { data: monthData } = await supabase
          .from('black_creator_current_month')
          .select('diamonds, live_days, live_duration_minutes, tier_current, tier_m1, is_active, objective_diamonds, cashback_amount, cashback_pct')
          .eq('tiktok_id', authTiktokId)
          .single();
        setCurrentMonth(monthData || null);

        const { data: histData } = await supabase
          .from('black_creator_monthly')
          .select('period, diamonds, tier_p, cashback, live_days, live_hours, tier_evolution')
          .eq('tiktok_id', authTiktokId)
          .order('period', { ascending: false })
          .limit(6);
        if (histData) setHistory(histData);
      } catch (err) {
        console.error('Cashback init error:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [authTiktokId, authLoading]);

  const stats = useMemo(() => {
    if (!currentMonth) return { diamonds: 0, liveDays: 0, liveHours: 0 };
    return {
      diamonds: currentMonth.diamonds || 0,
      liveDays: currentMonth.live_days || 0,
      liveHours: parseFloat(((currentMonth.live_duration_minutes || 0) / 60).toFixed(1)),
    };
  }, [currentMonth]);

  const cashbackInfo = useMemo(() => {
    const tierCurrent = currentMonth?.tier_current || 0;
    const tierM1 = currentMonth?.tier_m1 || 0;
    const diamonds = stats.diamonds;
    const liveDays = stats.liveDays;
    const liveHours = stats.liveHours;

    let type = null;
    let tiers = [];

    const dayOfMonth = new Date().getDate();
    const isEarlyMonth = dayOfMonth <= 7 && tierCurrent <= 1 && tierM1 > 1 && diamonds < 50000;

    if (isEarlyMonth) { type = 'early_month'; tiers = EVOLUTION_TIERS; }
    else if (tierCurrent > tierM1) { type = 'evolution'; tiers = EVOLUTION_TIERS; }
    else if (tierCurrent === tierM1 && diamonds > 200000) { type = 'maintien'; tiers = MAINTIEN_TIERS; }
    else if (tierCurrent === tierM1) { type = 'push_maintien'; tiers = MAINTIEN_TIERS; }
    else { type = 'dropped'; tiers = EVOLUTION_TIERS; }

    let currentTierIndex = -1;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (liveDays >= tiers[i].days && liveHours >= tiers[i].hours) { currentTierIndex = i; break; }
    }

    const currentPct = currentTierIndex >= 0 ? tiers[currentTierIndex].pct : 0;
    const nextTier = currentTierIndex < tiers.length - 1 ? tiers[currentTierIndex + 1] : null;

    let daysNeeded = 0, hoursNeeded = 0;
    if (nextTier) {
      daysNeeded = Math.max(0, nextTier.days - liveDays);
      hoursNeeded = Math.max(0, Math.ceil(nextTier.hours - liveHours));
    }

    const estimatedCashback = currentPct > 0 ? Math.round(diamonds * currentPct / 100) : 0;
    return { type, tiers, currentTierIndex, currentPct, nextTier, daysNeeded, hoursNeeded, estimatedCashback, tierCurrent, tierM1 };
  }, [currentMonth, stats]);

  const qs = isDebugMode && authTiktokId ? `?creator=${authTiktokId}` : '';

  if (loading) {
    return (
      <CreatorScreen>
        <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
          <div style={{
            width: 32, height: 32,
            border: '2px solid var(--accent-pink)', borderTopColor: 'transparent',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          }}/>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </CreatorScreen>
    );
  }

  const { tiers, currentTierIndex, currentPct, nextTier, daysNeeded, hoursNeeded, estimatedCashback, tierCurrent, tierM1, type } = cashbackInfo;
  const nextTierForProgress = nextTier || tiers[tiers.length - 1];
  const daysProgress = nextTierForProgress ? Math.min(100, (stats.liveDays / nextTierForProgress.days) * 100) : 100;
  const hoursProgress = nextTierForProgress ? Math.min(100, (stats.liveHours / nextTierForProgress.hours) * 100) : 100;
  const objectiveDiamonds = currentMonth?.objective_diamonds || 0;
  const diamondProgress = objectiveDiamonds > 0 ? Math.min(100, (stats.diamonds / objectiveDiamonds) * 100) : 0;

  const totalPaid = history.filter(h => h.cashback > 0).reduce((s, h) => s + (h.cashback || 0), 0);
  const paidMonths = history.filter(h => h.cashback > 0).length;
  const avgPaid = paidMonths > 0 ? Math.round(totalPaid / paidMonths) : 0;

  let statusPill;
  if (type === 'evolution') statusPill = <Pill color="green"><I.TrendUp size={11}/> {t('evolution')}</Pill>;
  else if (type === 'maintien') statusPill = <Pill color="cyan"><I.Check size={11}/> {t('maintien')}</Pill>;
  else if (type === 'push_maintien') statusPill = <Pill color="gold">{t('not_eligible')}</Pill>;
  else if (type === 'dropped') statusPill = <Pill color="red"><I.TrendDown size={11}/> {t('tier_dropped')}</Pill>;
  else statusPill = <Pill color="cyan"><I.Clock size={11}/> {t('cb_early_month')}</Pill>;

  return (
    <CreatorScreen>
      <div className="screen grain">
        <ScreenHeader
          title={t('cashback')}
          subtitle="NoiRock Program"
          onBack={() => navigate(`/my/dashboard${qs}`)}
        />

        {/* Hero — estimated cashback in diamonds */}
        <div style={{ padding: '0 20px' }}>
          <div style={{
            position: 'relative', overflow: 'hidden',
            background: 'linear-gradient(145deg, var(--accent-green-soft) 0%, var(--bg-2) 70%)',
            border: '1px solid oklch(0.78 0.17 155 / 0.25)',
            borderRadius: 28, padding: 22,
          }}>
            <div style={{
              position: 'absolute', top: -80, right: -80, width: 220, height: 220,
              background: 'radial-gradient(circle, oklch(0.78 0.17 155 / 0.3) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}/>
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 10, color: 'var(--accent-green)', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                  {t('estimated_cashback')}
                </div>
                {statusPill}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span className="mono tab" style={{ fontSize: 44, fontWeight: 800, letterSpacing: -2, lineHeight: 1, color: 'var(--fg-0)' }}>
                  {fmtNum(estimatedCashback)}
                </span>
                <DiamondGem size={24} color="var(--accent-green)"/>
                {currentPct > 0 && (
                  <span style={{ fontSize: 13, color: 'var(--accent-green)', fontWeight: 600 }}>
                    ({currentPct}%)
                  </span>
                )}
              </div>
              <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Pill color="neutral"><TierBadge tier={tierCurrent || 1} size="sm"/> T{tierM1}→T{tierCurrent}</Pill>
                <Pill color="neutral"><I.Calendar size={11}/> {stats.liveDays} {t('days_plus')}</Pill>
                <Pill color="neutral"><I.Clock size={11}/> {stats.liveHours}h</Pill>
              </div>
            </div>
          </div>
        </div>

        {/* Totals paid row */}
        {paidMonths > 0 && (
          <div style={{ padding: '14px 20px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Card pad={14}>
              <div style={{ fontSize: 10, color: 'var(--fg-3)', fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                {t('cb_total_paid') || 'Total paid'}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 4 }}>
                <span className="mono tab" style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.8 }}>{fmtD(totalPaid)}</span>
                <DiamondGem size={13} color="var(--accent-cyan)"/>
              </div>
              <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }}>{paidMonths} {t('months')}</div>
            </Card>
            <Card pad={14}>
              <div style={{ fontSize: 10, color: 'var(--fg-3)', fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                {t('avg_per_month')}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 4 }}>
                <span className="mono tab" style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.8 }}>{fmtD(avgPaid)}</span>
                <DiamondGem size={13} color="var(--accent-cyan)"/>
              </div>
            </Card>
          </div>
        )}

        {/* Progression */}
        <div style={{ padding: '20px 20px 0' }}>
          <SectionLabel>{t('cb_progression')}</SectionLabel>
          <Card pad={16}>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tiers.length}, 1fr)`, gap: 6, marginBottom: 14 }}>
              {tiers.map((tier, i) => {
                const isActive = i === currentTierIndex;
                const isPassed = i < currentTierIndex;
                const isFuture = i > currentTierIndex;
                return (
                  <div key={i} style={{
                    padding: '10px 4px', borderRadius: 10,
                    background: isActive ? 'var(--accent-green-soft)' : isPassed ? 'oklch(0.78 0.17 155 / 0.05)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${isActive ? 'oklch(0.78 0.17 155 / 0.4)' : 'var(--line-1)'}`,
                    textAlign: 'center', position: 'relative', opacity: isFuture ? 0.5 : 1,
                  }}>
                    {isActive && (
                      <div style={{
                        position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
                        fontSize: 8, padding: '1px 5px', borderRadius: 4,
                        background: 'var(--accent-green)', color: 'var(--bg-0)', fontWeight: 700, letterSpacing: 0.3,
                      }}>{t('current_label')}</div>
                    )}
                    <div className="mono tab" style={{ fontSize: 16, fontWeight: 700, color: isActive ? 'var(--accent-green)' : isPassed ? 'oklch(0.78 0.17 155 / 0.7)' : 'var(--fg-3)' }}>
                      {tier.pct}%
                    </div>
                    <div style={{ fontSize: 9, color: isActive ? 'var(--accent-green)' : 'var(--fg-4)', marginTop: 2 }}>
                      {tier.days}d · {tier.hours}h
                    </div>
                  </div>
                );
              })}
            </div>
            {nextTier && (
              <div style={{
                padding: '10px 12px', borderRadius: 12,
                background: 'var(--accent-gold-soft)',
                border: '1px solid oklch(0.82 0.14 85 / 0.25)',
                textAlign: 'center',
              }}>
                <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
                  {t('cb_to_next')}: <span style={{ color: 'var(--accent-gold)', fontWeight: 700 }}>+{daysNeeded}d · +{hoursNeeded}h</span>
                </span>
              </div>
            )}
          </Card>
        </div>

        {/* This month activity */}
        <div style={{ padding: '20px 20px 0' }}>
          <SectionLabel>{t('cb_this_month')}</SectionLabel>
          <Card pad={16}>
            {[
              { label: t('live_days'), val: stats.liveDays, goal: nextTierForProgress?.days, unit: '', pct: daysProgress, color: 'var(--accent-pink)' },
              { label: t('hours'), val: stats.liveHours, goal: nextTierForProgress?.hours, unit: 'h', pct: hoursProgress, color: 'var(--accent-gold)' },
              { label: t('diamonds'), val: fmtD(stats.diamonds), goal: objectiveDiamonds > 0 ? fmtD(objectiveDiamonds) : null, unit: '', pct: diamondProgress, color: 'var(--accent-cyan)' },
            ].map((row, i) => (
              <div key={i} style={{ marginBottom: i < 2 ? 14 : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--fg-2)', fontWeight: 500 }}>{row.label}</span>
                  <span className="mono tab" style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-0)' }}>
                    {row.val}{row.unit}{row.goal != null && <span style={{ fontSize: 10, color: 'var(--fg-3)', fontWeight: 500 }}> / {row.goal}{row.unit}</span>}
                  </span>
                </div>
                <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${row.pct}%`,
                    background: row.pct >= 100 ? 'var(--accent-green)' : row.color,
                    borderRadius: 3, transition: 'width 0.5s',
                  }}/>
                </div>
              </div>
            ))}
          </Card>
        </div>

        {/* History */}
        <div style={{ padding: '20px 20px 0' }}>
          <SectionLabel>{t('cb_history')}</SectionLabel>
          {history.length === 0 ? (
            <Card pad={30} style={{ textAlign: 'center', color: 'var(--fg-3)', fontSize: 13 }}>
              {t('no_data')}
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.map((row, i) => {
                const tierEvo = row.tier_evolution;
                const evoLabel = tierEvo === 'up' ? '↑' : tierEvo === 'down' ? '↓' : '→';
                const evoColor = tierEvo === 'up' ? 'var(--accent-green)' : tierEvo === 'down' ? 'var(--accent-red)' : 'var(--fg-3)';
                const cashbackVal = row.cashback || 0;
                return (
                  <Card key={i} pad={14} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 12,
                      background: 'var(--bg-3)', border: '1px solid var(--line-1)',
                      display: 'grid', placeItems: 'center',
                    }}>
                      <DiamondGem size={18} color="var(--accent-cyan)"/>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{formatPeriod(row.period)}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: evoColor }}>{evoLabel}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
                        T{row.tier_p || '—'} · {row.live_days ?? '—'} {t('days_plus')}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="mono tab" style={{ fontSize: 14, fontWeight: 700, color: cashbackVal > 0 ? 'var(--accent-green)' : 'var(--fg-3)' }}>
                        {cashbackVal > 0 ? fmtD(cashbackVal) : '—'}
                      </div>
                      <div style={{ fontSize: 9, color: cashbackVal > 0 ? 'var(--accent-green)' : 'var(--fg-4)' }}>
                        {cashbackVal > 0 ? 'cashback' : '—'}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* View rules CTA */}
        <div style={{ padding: '20px 20px 0' }}>
          <Link to={`/my/rules${qs}`} style={{ textDecoration: 'none' }}>
            <Card pad={14} style={{
              background: 'var(--accent-pink-soft)',
              border: '1px solid oklch(0.68 0.22 10 / 0.25)',
              textAlign: 'center',
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-pink)' }}>
                {t('cb_view_rules')}
              </span>
            </Card>
          </Link>
        </div>
      </div>

      <BottomNav active="cashback" qs={qs}/>
    </CreatorScreen>
  );
};

export default CreatorCashback;
